import { fetchAllPages, fetchList, fetchOne, updateOne } from '@/shared/api/jsonapi/client';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { OBLIGATION_ROUTE } from '@/features/obligations/obligationsApi';
import type { ObligationKind } from '@/features/obligations/obligationRules';

/** Risorsa `notifications` (BE `Notification`). Attributi reali camelCase sul wire:
 *  lo stato di lettura è `read` (non isRead), il timestamp è `dateSent`, il soft-delete è `erased`. */
export interface NotificationRow extends Denormalized {
  type: 'notifications';
  id: string;
  title?: string;
  body?: string;
  /** Letta? (BE `Read`). */
  read?: boolean;
  /** Soft-delete (BE `Erased`). */
  erased?: boolean;
  dateSent?: string;
  dateRead?: string;
  /** Tipo di push (es. `circularPublished`) usato per derivare icona/CTA. */
  pushType?: string;
  /** JSON string con payload contestuale (es. `{ changelogId, version, status }`). */
  data?: string;
  /** Non esposti dal BE ma tenuti opzionali per retrocompatibilità dei consumer. */
  category?: string;
  link?: string;
}

/**
 * Dove porta una notifica, dedotto dal `pushType` (il BE non manda un `link`).
 *
 * ## Perché per **famiglia** e non per valore esatto
 *
 * Il backend compone i tipi a runtime — `leaveApproved`, `leaveRejected`, `leaveDayCancelledByHr`,
 * `smartWorkingApproved`, `expenseReportPaid`, e così via — quindi un elenco di stringhe esatte
 * nasce già incompleto e invecchia a ogni flusso nuovo. Qui si guarda il prefisso: tutta la
 * famiglia «assenze» porta alle assenze, e un tipo mai visto prima ma che si chiama `leaveQualcosa`
 * arriva a destinazione da solo.
 *
 * Le destinazioni sono solo rotte che **esistono**: le prenotazioni, per dire, non hanno una
 * pagina indice, quindi non sono in mappa — meglio nessun collegamento che uno che apre una
 * schermata vuota.
 */
export function notificationLink(n: Pick<NotificationRow, 'link' | 'pushType' | 'data'>): string | undefined {
  if (n.link) return n.link;
  const t = n.pushType ?? '';
  /* Circolari (task 2055): la casella, con la circolare già aperta — le note di rilascio (quelle
     con la versione nel payload) sulla loro pagina. Le notifiche del vecchio changelog lì. */
  if (t === 'circularPublished') return circularFromPayload(n.data) ?? '/circulars';
  /* Il flusso di chi scrive e controlla (task 2062): revisione chiesta, tornata indietro, approvata,
     pronta da pubblicare, e i due solleciti. Portano al registro, non alla casella: la circolare non
     è ancora uscita. */
  if (CIRCULAR_FLOW_PUSH.has(t)) return circularInRegister(n.data) ?? '/circulars/register';
  if (t.startsWith('changelog')) return '/circulars/release-notes';
  /* Variazione anagrafica chiesta da un cliente (task 2056): la scheda del cliente, sulle richieste. */
  if (t.startsWith('clientChangeRequest')) return clientChangeFromPayload(n.data) ?? '/approvals?tab=clientChange';
  /* Le famiglie che il BE emette componendo il nome: `leaveApproved`, `smartWorkingRejected`,
     `taskAssigned`… Senza queste righe la maggioranza delle notifiche restava senza strada. */
  /* Un responsabile cessato ha ancora sottoposti (task 1998, all'ufficio del personale una volta
     per cessazione): l'elenco da sistemare della Gerarchia. */
  if (t === 'ceasedManagerSubordinates') return '/organization/hierarchy?focus=manager-inactive';
  if (t.startsWith('leave') || t === 'vac') return '/leave';
  if (t.startsWith('smartWorking')) return '/smart-working';
  /* Il riepilogo scostamenti è di un progetto: l'elenco di tutti i task non diceva quale. */
  if (t === 'task_drift_digest') return linkDalPayload(n.data) ?? projectFromPayload(n.data) ?? '/tasks';
  if (isTaskPushType(t)) return taskFromPayload(n.data) ?? '/tasks';
  // La segnalazione del collaudo è un task, e il payload ne porta l'id.
  if (t.startsWith('qa')) return taskFromPayload(n.data) ?? '/test-plans';
  if (t.startsWith('timesheet')) return '/timesheet';
  // Il sollecito a chi sta sopra riguarda i suoi, non lui.
  if (t === 'obligationEscalation') return '/team-deadlines';
  if (t.startsWith('obligation')) return obligationFromPayload(n.data);
  if (t.startsWith('attendance') || t.startsWith('ATTENDANCE')) return '/attendance';
  /*
   * L'assistenza sui task (BE `SUPPORT-NOTIFICATIONS-v2`, deploy 1 set 2026). Sette `pushType`, e due
   * destinazioni diverse perché sono due pubblici diversi:
   *
   *  · `Opened` avvisa **la coda** che è arrivata una richiesta → lo smistamento è dove si lavora
   *    ciò che il server non ha saputo instradare da sé;
   *  · `Replied` avvisa **l'assegnatario** che il cliente ha risposto → il ticket è un task, e il
   *    modulo task è la destinazione più precisa che abbiamo (non c'è ancora un indirizzo per il
   *    singolo ticket: `/tickets` non ha un parametro e `/tasks/$taskId` su desktop rimanda
   *    all'elenco);
   *  · gli altri sei sono i momenti rivolti al **richiedente** (`…Waiting` dal 29-09-2026, in attesa) (`…Rejected` dal 24-09-2026, la
   *    richiesta non accolta): partono per e-mail — chi scrive
   *    dalla porta pubblica non è un'utenza — e **anche in-app quando ha un accesso di portale
   *    vivo** (BE v1.175.0, su nostra richiesta). Quel campanello legge la stessa tabella e
   *    proietta il solo `pushType`, quindi là questa riga è la sola destinazione disponibile.
   *
   * Sta accanto al vecchio `helpDeskResp`, che è la cosa che sostituisce, e non collide con
   * `t.startsWith('task')` qui sopra: nessuno di questi sei comincia per «task».
   */
  if (t.startsWith('supportRequest')) {
    /*
     * ⚠️ Qui il `Data` viene **prima** del `pushType`, al contrario di tutte le altre famiglie, e
     * la ragione è che sulle due notifiche allo staff il BE ci manda una destinazione **più
     * precisa** di quella che il tipo può esprimere: `{"link":"/tasks","taskId":…,"taskNumber":…}`
     * quando il ticket esiste, `{"link":"/support-intake",…}` quando la richiesta è **ancora in
     * coda** — e senza un task il posto giusto è lo smistamento, non un ticket che non c'è.
     * Preferendo il tipo mandremmo alla pagina sbagliata metà delle volte.
     */
    const daPayload = linkDalPayload(n.data);
    // `/tasks` col taskId: si apre il task, non l'elenco.
    if (daPayload === '/tasks') return taskFromPayload(n.data) ?? daPayload;
    if (daPayload) return daPayload;
    if (t === 'supportRequestOpened') return '/support-intake';
    if (t === 'supportRequestReplied') return taskFromPayload(n.data) ?? '/tasks';
    return '/portal/assistenza';
  }
  if (t === 'helpDeskResp') return '/help';
  if (t === 'portal_document_published') return '/portal/documenti';
  if (t === 'portal_site_report_created') return '/portal/attivita';
  /* `expenseReportSubmitted|Approved|Rejected|Paid` (BE v1.121.1). Senza questa riga la
     notifica arrivava senza CTA: chi la riceve deve sapere dove andare, e la pagina è una
     sola sia per il titolare sia per chi deve decidere. */
  if (t.startsWith('expenseReport')) return '/expense-reports';
  /* `ATTENDANCE_MISSED_PUNCH` (BE v1.142.0): il promemoria di timbratura mancata. Il `data`
     porta `{ reminderType, date, employeeId, anchorAt, reminderId }` ma **non** un link, quindi
     senza questa riga l'avviso arriva senza CTA — cioè dice «non hai timbrato» e non porta dove
     si timbra. La destinazione è la pagina delle presenze, dove si regolarizza la giornata. */
  if (t === 'ATTENDANCE_MISSED_PUNCH') return '/attendance';
  // Payload esplicito: alcune notifiche possono trasportare un link/route nel Data JSON.
  return linkDalPayload(n.data);
}

/**
 * Il percorso relativo dentro il JSON di `Data`, sotto `link` | `route` | `url`.
 *
 * Estratto perché lo usa anche la famiglia dell'assistenza, che lo consulta **prima** del suo tipo
 * (vedi il commento là): due copie della stessa lettura divergerebbero al primo campo aggiunto.
 * Tutto ciò che non è un percorso relativo si ignora — un `link` assoluto in un payload sarebbe una
 * navigazione fuori dall'app, e non è una cosa che una notifica deve poter decidere.
 */
/** I tipi di `TaskNotificationService`: `task_*`, `comment_reply`, `issue_*`. */
function isTaskPushType(t: string): boolean {
  return t.startsWith('task') || t === 'comment_reply' || t.startsWith('issue_');
}

/** `{ taskId }` nel payload: il task aperto nella sezione. */
function taskFromPayload(data?: string | null): string | undefined {
  if (!data) return undefined;
  try {
    const id = (JSON.parse(data) as Record<string, unknown>).taskId;
    if (typeof id === 'string' && id) return `/tasks?task=${encodeURIComponent(id)}`;
  } catch { /* data non-JSON: ignora */ }
  return undefined;
}

/** `{ circularId, version }` nel payload: la circolare, aperta nella casella del suo tipo. */
const CIRCULAR_FLOW_PUSH = new Set([
  'circularReviewRequested', 'circularReturned', 'circularApproved', 'circularReadyToPublish',
  'circularReviewReminder', 'circularReviewEscalated',
]);

/** `{ circularId }` nel payload: la circolare aperta nel registro. */
function circularInRegister(data?: string | null): string | undefined {
  if (!data) return undefined;
  try {
    const id = (JSON.parse(data) as Record<string, unknown>).circularId;
    if (typeof id === 'string' && id) return `/circulars/register?open=${encodeURIComponent(id)}`;
  } catch { /* data non-JSON: ignora */ }
  return undefined;
}

function circularFromPayload(data?: string | null): string | undefined {
  if (!data) return undefined;
  try {
    const { circularId: id, version } = JSON.parse(data) as Record<string, unknown>;
    const page = typeof version === 'string' && version ? '/circulars/release-notes' : '/circulars';
    if (typeof id === 'string' && id) return `${page}?open=${encodeURIComponent(id)}`;
  } catch { /* data non-JSON: ignora */ }
  return undefined;
}

/** `{ clientId }` nel payload: le richieste di modifica di quel cliente. */
function clientChangeFromPayload(data?: string | null): string | undefined {
  if (!data) return undefined;
  try {
    const id = (JSON.parse(data) as Record<string, unknown>).clientId;
    if (typeof id === 'string' && id) return `/clients/${encodeURIComponent(id)}?tab=changes`;
  } catch { /* data non-JSON: ignora */ }
  return undefined;
}

/** `{ items: [{ kind }] }`: un tipo solo porta alla sua pagina, più tipi alla Home. */
function obligationFromPayload(data?: string | null): string {
  if (!data) return '/';
  try {
    const items = (JSON.parse(data) as { items?: Array<{ kind?: string }> }).items ?? [];
    const kinds = new Set(items.map((i) => i.kind).filter(Boolean));
    if (kinds.size === 1) {
      const route = OBLIGATION_ROUTE[[...kinds][0] as ObligationKind];
      if (route) return route;
    }
  } catch { /* data non-JSON: ignora */ }
  return '/';
}

/** `{ projectId }` nel payload: la scheda di quel progetto. */
function projectFromPayload(data?: string | null): string | undefined {
  if (!data) return undefined;
  try {
    const id = (JSON.parse(data) as Record<string, unknown>).projectId;
    if (typeof id === 'string' && id) return `/projects/${encodeURIComponent(id)}`;
  } catch { /* data non-JSON: ignora */ }
  return undefined;
}

function linkDalPayload(data?: string | null): string | undefined {
  if (!data) return undefined;
  try {
    const parsed = JSON.parse(data) as Record<string, unknown>;
    const link = parsed.link ?? parsed.route ?? parsed.url;
    if (typeof link === 'string' && link.startsWith('/')) return link;
  } catch { /* data non-JSON: ignora */ }
  return undefined;
}

/**
 * Le **mie** notifiche, dalla più recente.
 *
 * ⚠️ Il filtro sull'utente lo mette il client, e non è una precauzione ridondante: `Notification`
 * non implementa `IHasTenant` — è dichiarato nell'entità, la sua `TenantId` è `[NotMapped]` — e
 * `NotificationsController` non sovrascrive il GET. Non c'è quindi nessun `HasQueryFilter` che
 * restringa la lista a chi la chiede: senza `equals(user.id,…)` il server risponde con le prime
 * cinquanta righe della tabella, di chiunque siano.
 *
 * Ed è la stessa riga che spegneva la campanella: quelle cinquanta righe erano le più vecchie —
 * senza `sort` l'ordine è quello del database — quindi già lette, e il pallino non compariva mai
 * mentre gli avvisi nuovi restavano fuori dalla finestra.
 *
 * `dateSent` è un `[Attr]` pieno sull'entità, quindi ordinabile: il commento precedente diceva il
 * contrario, ed era quello a tenere l'ordinamento lato client su una pagina già sbagliata.
 */
export async function fetchMyNotifications(userId: string) {
  return fetchList<NotificationRow>('notifications', {
    filter: { 'user.id': { eq: userId }, erased: { eq: false } },
    sort: ['-dateSent'],
    page: { size: 50 },
    fields: {
      notifications: ['title', 'body', 'read', 'erased', 'dateSent', 'dateRead', 'pushType', 'data'],
    },
  });
}

/** Il tetto della pagina completa: oltre, la pagina lo dice. */
export const ALL_NOTIFICATIONS_CAP = 2000;

/** Tutte le mie, per la pagina completa: i filtri lavorano su queste. */
export async function fetchAllMyNotifications(userId: string) {
  return fetchAllPages<NotificationRow>('notifications', {
    filter: { 'user.id': { eq: userId }, erased: { eq: false } },
    sort: ['-dateSent'],
    fields: {
      notifications: ['title', 'body', 'read', 'erased', 'dateSent', 'dateRead', 'pushType', 'data'],
    },
  }, ALL_NOTIFICATIONS_CAP);
}

/**
 * Quante ne restano da leggere — chiesto al server, non contato sulla pagina.
 *
 * Il pallino deve dire il vero anche quando le non lette sono più di cinquanta: contarle
 * sull'elenco caricato darebbe un numero che si ferma alla finestra, e un contatore che si
 * ferma è peggio di nessun contatore.
 */
export async function fetchUnreadCount(userId: string): Promise<number> {
  const r = await fetchList<NotificationRow>('notifications', {
    filter: { 'user.id': { eq: userId }, erased: { eq: false }, read: { eq: false } },
    page: { size: 1 },
    // Di questa lettura si usa SOLO `meta.total`: un attributo basta a non tirar giu' la riga.
    fields: { notifications: ['read'] },
  });
  return r.total;
}

/** Una notifica sola, per la sua pagina di dettaglio. */
export async function fetchNotification(id: string) {
  return fetchOne<NotificationRow>('notifications', id);
}

/** Segna letta: PATCH `read=true` (+ dateRead). */
export async function markAsRead(id: string) {
  return updateOne<NotificationRow>('notifications', { id, attributes: { read: true } });
}

/** Torna da leggere: PATCH `read=false`, e la data di lettura si azzera. */
export async function markAsUnread(id: string) {
  return updateOne<NotificationRow>('notifications', { id, attributes: { read: false, dateRead: null } });
}

/** Elimina (soft-delete): PATCH `erased=true`. */
export async function eraseNotification(id: string) {
  return updateOne<NotificationRow>('notifications', { id, attributes: { erased: true } });
}
